import { Colors, FlexBox, Skeleton, useBreakpoint } from '@brg/ui';
import { FC, ReactNode, useMemo, useState } from 'react';
import { PageControl, PageNumber } from './styles';
import { ReactComponent as BackSvg } from './assets/back.svg';
import { ReactComponent as NextSvg } from './assets/next.svg';
import styled from 'styled-components';
import { Input } from "@brg/input";
import { LayoutConst } from 'core';

export interface PaginationLayoutProps {
    isLoading?: boolean;
    paging?: ReactNode;
    button?: ReactNode;
    hidden?: boolean;
    disableOffset?: boolean;
}

const PaginationRoot = styled(FlexBox)<{ hidden?: boolean, disableOffset?: boolean }>`
    margin-top: 64px !important;
    margin-bottom: ${({ disableOffset }) => disableOffset ? 0 : '104px' };
    flex-wrap: wrap;
    height: 32px;
    max-height: 32px;
    flex-flow: row;

    button {
        height: 32px !important;
        max-height: 32px;
        max-width: 171px;
        // width: 171px;
        min-width: 101px;
        font-size: 13px;
        line-height: 16px;
    }

    .pagging_item {
        font-size: 13px;
        line-height: 16px;
    }

    visibility: ${p => p.hidden ? 'hidden' : 'visible'};
    
    @media (max-width: ${LayoutConst.screen.m}px) {
        justify-content: space-between;
    }
    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        justify-content: center;
        flex-flow: column;

        > div:first-child {
            margin-bottom: 16px;
        }
    }
    @media (max-width: ${LayoutConst.screen.sm}px) {
      margin-bottom: ${({ disableOffset }) => disableOffset ? '24px' : '70px' };
    }
`

/**
 * Layout компонент переключателя страниц
 */
export const PaginationLayout: FC<PaginationLayoutProps> = (props) => {
    const { isLoading, paging, button, hidden, disableOffset } = props;

    return (
        <PaginationRoot
            style={{ minWidth: 'calc(100% - 20px)' }}
            gy={46}
            width="100%"
            justify="center"
            align="center"
            hidden={hidden}
            disableOffset={disableOffset}
        >
            {!isLoading && button}
            {isLoading ? (
                <FlexBox gx={8}>
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                </FlexBox>
            ) : (
                paging
            )}
        </PaginationRoot>
    );
};

export const DotsContainer = styled(FlexBox)`
    justify-content: center;
    align-items: center;
    height: 32px;
    border-radius: 3px;
    color: ${Colors.geoWhite47};
    user-select: none;
`;

export const PageInputWrapper = styled(FlexBox)`
    position: relative;
    height: 32px !important;
    max-height: 32px !important;
    max-width: 65px;
    width: 65px;
    background-color: ${({ theme }) => theme.inputs.pagination.mainPagination.pageBg.bg};
    border-radius: 3px;
    margin-right: 8px;
`;

export const PageInput = styled(Input.Text)`
    height: 100%;
    //width: calc(100% / 2);
    background-color: transparent !important;
    outline-color: transparent;
    color: ${({ theme }) => theme.app.text};

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    input {
        text-align: center;
        border: none !important;
        background-color: transparent;
        outline: none;
        padding: 0 !important;
    }


    transition: all 0.2s;
    
    font-size: 13px;
    line-height: 16px;

    @media (max-width: ${LayoutConst.screen.m}px) {
        height: 28px !important;
        font-size: 13px;
        input {
            padding: 0 12px;
        }
    }
`;

const PaggingItemWrapper = styled(FlexBox)`
    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
        // > :not(:first-child) {
        //     margin-left: auto;
        // }
    }
`;

export interface PaginationProps {
    isLoading?: boolean;
    page: number;
    pages: number;
    onPageChange?: (page: number) => void;
    isCanLoadMore?: boolean;
    loadMore?: () => void;
    showLoadMore?: boolean;
    hidden?: boolean;
    disableOffset?: boolean;
}

const clamp = (min: number, value: number, max: number) => {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}

/**
 * Компонент пагинации
 *
 * @param props
 * @returns
 */
export const SimplePagination: FC<PaginationProps> = (props) => {
    const { isLoading, page, onPageChange, pages, hidden } = props;
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const backStep = isMobile ? 2 : 2;
    const forwardStep = isMobile ? 2 : 2;
    const [pageText, setPageText] = useState<number>(page);
    // Построение индексов массива
    const pagesList = useMemo(() => {
        const arr = Array<number>();
        const startStep = page >= pages - backStep ? pages - (backStep + forwardStep + 1) : page - backStep;
        const endStep = page <= forwardStep ? (backStep + forwardStep + 1) : page + forwardStep;
        const start = clamp(1, startStep, pages);
        const end = clamp(1, endStep, pages);
        for (let i = start; i <= end; i++) {
            arr.push(i);
        }
        return arr;
    }, [page, pages, backStep, forwardStep]);

    const onPageChangeCreate = (p: number) => () => {
        onPageChange?.(p);
    };
    
    const showLast = pages - page > backStep;

    const showNext = useMemo(() => (page < pages), [page, pages])

    // Отрисовка
    return (
        <PaginationLayout
            isLoading={isLoading}
            disableOffset={props.disableOffset}
            paging={
                pages > 1 && (
                  <>
                    <PaggingItemWrapper gx={8} justify='center'>
                        {page > forwardStep + 1 && (
                          <>
                            <PageControl onClick={onPageChangeCreate(1)}>
                                <BackSvg />
                            </PageControl>
                            <DotsContainer>
                            ...
                            </DotsContainer>
                          </>
                        )}
                        {pagesList.filter((i: number) => {
                            if (showLast && page < pages - backStep - 1) {
                                return i !== pagesList?.length;
                            }
                            return i;
                        }).map((value, index) => (
                            <div key={`pi_${value}_ ${index}`} className='pagging_item'>
                                {value > 0 ? (
                                    <PageNumber onClick={onPageChangeCreate(value)} active={value === page}>
                                        {value}
                                    </PageNumber>
                                ) : null}
                            </div>
                        ))}
                        {showLast && (
                            <>
                                {page < pages - backStep - 1 && (
                                    <>
                                        <DotsContainer>
                                            ...
                                        </DotsContainer>
                                        <PageNumber onClick={onPageChangeCreate(pages)} active={false}>
                                                {pages}
                                        </PageNumber>
                                    </>
                                )}
                            </>
                        )}
                        {showNext && (
                            <PageControl onClick={onPageChangeCreate(page + 1)}>
                                <NextSvg />
                            </PageControl>
                        )}
                    </PaggingItemWrapper>
                  </>
                )
            }
            hidden={hidden}
        />
    );
};
import { Colors, FlexBox, Skeleton, useBreakpoint } from '@brg/ui';
import { FC, ReactNode, useMemo, useState } from 'react';
import { PageControl, PageNumber } from './styles';
import { ReactComponent as BackSvg } from './assets/back.svg';
import { ReactComponent as NextSvg } from './assets/next.svg';
import styled from 'styled-components';
import { Input } from "@brg/input";
import { LayoutConst } from 'core';

export interface PaginationLayoutProps {
    isLoading?: boolean;
    paging?: ReactNode;
    button?: ReactNode;
    hidden?: boolean;
    disableOffset?: boolean;
}

const PaginationRoot = styled(FlexBox)<{ hidden?: boolean, disableOffset?: boolean }>`
    margin-top: 64px !important;
    margin-bottom: ${({ disableOffset }) => disableOffset ? 0 : '104px' };
    flex-wrap: wrap;
    height: 32px;
    max-height: 32px;
    flex-flow: row;

    button {
        height: 32px !important;
        max-height: 32px;
        max-width: 171px;
        // width: 171px;
        min-width: 101px;
        font-size: 13px;
        line-height: 16px;
    }

    .pagging_item {
        font-size: 13px;
        line-height: 16px;
    }

    visibility: ${p => p.hidden ? 'hidden' : 'visible'};
    
    @media (max-width: ${LayoutConst.screen.m}px) {
        justify-content: space-between;
    }
    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        justify-content: center;
        flex-flow: column;

        > div:first-child {
            margin-bottom: 16px;
        }
    }
    @media (max-width: ${LayoutConst.screen.sm}px) {
      margin-bottom: ${({ disableOffset }) => disableOffset ? '24px' : '70px' };
    }
`

/**
 * Layout компонент переключателя страниц
 */
export const PaginationLayout: FC<PaginationLayoutProps> = (props) => {
    const { isLoading, paging, button, hidden, disableOffset } = props;

    return (
        <PaginationRoot
            style={{ minWidth: 'calc(100% - 20px)' }}
            gy={46}
            width="100%"
            justify="center"
            align="center"
            hidden={hidden}
            disableOffset={disableOffset}
        >
            {!isLoading && button}
            {isLoading ? (
                <FlexBox gx={8}>
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                    <Skeleton width={32} height={32} />
                </FlexBox>
            ) : (
                paging
            )}
        </PaginationRoot>
    );
};

export const DotsContainer = styled(FlexBox)`
    justify-content: center;
    align-items: center;
    height: 32px;
    border-radius: 3px;
    color: ${Colors.geoWhite47};
    user-select: none;
`;

export const PageInputWrapper = styled(FlexBox)`
    position: relative;
    height: 32px !important;
    max-height: 32px !important;
    max-width: 65px;
    width: 65px;
    background-color: ${({ theme }) => theme.inputs.pagination.mainPagination.pageBg.bg};
    border-radius: 3px;
    margin-right: 8px;
`;

export const PageInput = styled(Input.Text)`
    height: 100%;
    //width: calc(100% / 2);
    background-color: transparent !important;
    outline-color: transparent;
    color: ${({ theme }) => theme.app.text};

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    input {
        text-align: center;
        border: none !important;
        background-color: transparent;
        outline: none;
        padding: 0 !important;
    }


    transition: all 0.2s;
    
    font-size: 13px;
    line-height: 16px;

    @media (max-width: ${LayoutConst.screen.m}px) {
        height: 28px !important;
        font-size: 13px;
        input {
            padding: 0 12px;
        }
    }
`;

const PaggingItemWrapper = styled(FlexBox)`
    @media (max-width: ${LayoutConst.screen.s}px) {
        width: 100%;
        // > :not(:first-child) {
        //     margin-left: auto;
        // }
    }
`;

export interface PaginationProps {
    isLoading?: boolean;
    page: number;
    pages: number;
    onPageChange?: (page: number) => void;
    isCanLoadMore?: boolean;
    loadMore?: () => void;
    showLoadMore?: boolean;
    hidden?: boolean;
    disableOffset?: boolean;
}

const clamp = (min: number, value: number, max: number) => {
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
}

/**
 * Компонент пагинации
 *
 * @param props
 * @returns
 */
export const SimplePagination: FC<PaginationProps> = (props) => {
    const { isLoading, page, onPageChange, pages, hidden } = props;
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const backStep = isMobile ? 2 : 2;
    const forwardStep = isMobile ? 2 : 2;
    const [pageText, setPageText] = useState<number>(page);
    // Построение индексов массива
    const pagesList = useMemo(() => {
        const arr = Array<number>();
        const startStep = page >= pages - backStep ? pages - (backStep + forwardStep + 1) : page - backStep;
        const endStep = page <= forwardStep ? (backStep + forwardStep + 1) : page + forwardStep;
        const start = clamp(1, startStep, pages);
        const end = clamp(1, endStep, pages);
        for (let i = start; i <= end; i++) {
            arr.push(i);
        }
        return arr;
    }, [page, pages, backStep, forwardStep]);

    const onPageChangeCreate = (p: number) => () => {
        onPageChange?.(p);
    };
    
    const showLast = pages - page > backStep;

    const showNext = useMemo(() => (page < pages), [page, pages])

    // Отрисовка
    return (
        <PaginationLayout
            isLoading={isLoading}
            disableOffset={props.disableOffset}
            paging={
                pages > 1 && (
                  <>
                    <PaggingItemWrapper gx={8} justify='center'>
                        {page > forwardStep + 1 && (
                          <>
                            <PageControl onClick={onPageChangeCreate(1)}>
                                <BackSvg />
                            </PageControl>
                            <DotsContainer>
                            ...
                            </DotsContainer>
                          </>
                        )}
                        {pagesList.filter((i: number) => {
                            if (showLast && page < pages - backStep - 1) {
                                return i !== pagesList?.length;
                            }
                            return i;
                        }).map((value, index) => (
                            <div key={`pi_${value}_ ${index}`} className='pagging_item'>
                                {value > 0 ? (
                                    <PageNumber onClick={onPageChangeCreate(value)} active={value === page}>
                                        {value}
                                    </PageNumber>
                                ) : null}
                            </div>
                        ))}
                        {showLast && (
                            <>
                                {page < pages - backStep - 1 && (
                                    <>
                                        <DotsContainer>
                                            ...
                                        </DotsContainer>
                                        <PageNumber onClick={onPageChangeCreate(pages)} active={false}>
                                                {pages}
                                        </PageNumber>
                                    </>
                                )}
                            </>
                        )}
                        {showNext && (
                            <PageControl onClick={onPageChangeCreate(page + 1)}>
                                <NextSvg />
                            </PageControl>
                        )}
                    </PaggingItemWrapper>
                  </>
                )
            }
            hidden={hidden}
        />
    );
};
